import type { ReactNode } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import type { KnowledgeLawCountry } from '../../services/knowledgeLawCountries';
import type { BillingSubscriptionProduct } from '../../types/billing';
import { Loader2 } from '../Icons';
import { BrandButton } from '../ui/BrandButton';
import { ErrorNotice } from '../ui/ErrorNotice';
import { PricingFeatureList } from './PricingFeatureList';
import { PaidPricingCard } from './PaidPricingCard';
import { PricingCard, PricingCardHeader } from './PricingCard';

export function EnterprisePricingCard({ product, onSelect, onPlanIntent, countries, monthsSaved = 0, isLoading = false, hasError = false, compact = false, controls, isSubmitting = false }: {
  product?: BillingSubscriptionProduct;
  onSelect: () => void;
  onPlanIntent?: () => void;
  countries: KnowledgeLawCountry[];
  monthsSaved?: number;
  isLoading?: boolean;
  hasError?: boolean;
  compact?: boolean;
  controls?: ReactNode;
  isSubmitting?: boolean;
}) {
  const { t } = useTranslation();
  if (product) return <PaidPricingCard product={product} countries={countries} onSelect={onSelect} onPlanIntent={onPlanIntent} monthsSaved={monthsSaved} compact={compact} controls={controls} isSubmitting={isSubmitting} />;
  return (
    <PricingCard plan="enterprise" onSelect={() => undefined} compact={compact}>
      {!compact && <div className="hidden md:mb-3 md:block md:min-h-8" aria-hidden="true" />}
      <PricingCardHeader compact={compact} controls={controls} title={t('billing.landing.enterprise.title')} price={
        isLoading ? <div role="status" aria-label={t('billing.landing.loading')}><Loader2 className="h-7 w-7 animate-spin motion-reduce:animate-none" /></div>
          : hasError ? <ErrorNotice>{t('billing.catalogError')}</ErrorNotice>
            : <p className="text-sm text-slate-300">{t('billing.onboarding.noPlans')}</p>
      } />
      <PricingFeatureList plan="enterprise" countries={countries} includedOnly={compact} compact={compact} />
      <div className={`w-full ${compact ? 'mt-5' : 'mt-auto pt-8'}`}>
        <BrandButton variant="primary" size="lg" className="min-h-12 w-full justify-center" disabled>{t('billing.landing.selectEnterprise')}</BrandButton>
      </div>
    </PricingCard>
  );
}
